import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getTierByPriceId, type SubscriptionTierKey } from "@/lib/subscription-tiers";
import { useVisibilityPolling } from "@/hooks/useVisibilityPolling";

// Cache subscription_end in localStorage for 24h to avoid hitting the
// check-subscription edge function (and Stripe) on every auth event.
const SUB_END_CACHE_KEY = "sf_sub_end_cache_v1";
const SUB_END_TTL_MS = 24 * 60 * 60 * 1000;

function readCachedSubEnd(userId: string): string | null {
  try {
    const raw = localStorage.getItem(SUB_END_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { userId: string; subscriptionEnd: string | null; ts: number };
    if (parsed.userId !== userId) return null;
    if (Date.now() - parsed.ts > SUB_END_TTL_MS) return null;
    return parsed.subscriptionEnd;
  } catch {
    return null;
  }
}

function writeCachedSubEnd(userId: string, subscriptionEnd: string | null) {
  try {
    localStorage.setItem(
      SUB_END_CACHE_KEY,
      JSON.stringify({ userId, subscriptionEnd, ts: Date.now() })
    );
  } catch {
    /* ignore */
  }
}

type AppRole = "super_admin" | "support_manager" | "support_staff" | "user" | "demo_user" | "sales_rep";

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  industry: string | null;
  subscription_tier: string;
  subscription_active: boolean;
  subscription_status: string | null;
  has_stripe_customer?: boolean;
  proposals_used_this_month: number;
  is_suspended: boolean;
  created_at: string;
}

interface SubscriptionState {
  subscribed: boolean;
  tier: SubscriptionTierKey | null;
  status: string | null;
  subscriptionEnd: string | null;
  isLoading: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  isLoading: boolean;
  isSuperAdmin: boolean;
  isSupportRole: boolean;
  isSalesRole: boolean;
  isDemoUser: boolean;
  subscription: SubscriptionState;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState>({
    subscribed: false,
    tier: null,
    status: null,
    subscriptionEnd: null,
    isLoading: true,
  });

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, user_id, full_name, email, avatar_url, industry, subscription_tier, subscription_active, subscription_status, proposals_used_this_month, is_suspended, created_at")
      .eq("user_id", userId)
      .single();
    // `has_stripe_customer` is fetched lazily by the Pricing page now —
    // calling the RPC on every auth event burned Cloud usage for no benefit.
    setProfile(data as Profile | null);
    return data as Profile | null;
  };

  const fetchRoles = async (userId: string): Promise<AppRole[]> => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    const fetched = (data ?? []).map((r: any) => r.role as AppRole);
    setRoles(fetched);
    return fetched;
  };

  // Derive subscription state from profile fields (set by webhook)
  // Falls back to check-subscription edge function for subscription_end
  const deriveSubscription = useCallback(async (currentProfile: Profile | null, currentRoles: AppRole[] = []) => {
    setSubscription(prev => ({ ...prev, isLoading: true }));

    if (!currentProfile) {
      setSubscription({ subscribed: false, tier: null, status: null, subscriptionEnd: null, isLoading: false });
      return;
    }

    const isDemo = currentRoles.includes("demo_user");

    // Demo users: derive entirely from profile, never call Stripe
    if (isDemo) {
      const tier = (currentProfile.subscription_tier as SubscriptionTierKey) || "starter";
      setSubscription({
        subscribed: true,
        tier,
        status: "active",
        subscriptionEnd: null,
        isLoading: false,
      });
      return;
    }

    const status = currentProfile.subscription_status;
    const isActive = status === "active" || status === "trialing" || status === "pending";
    const tier = (currentProfile.subscription_tier as SubscriptionTierKey) || null;

    // If webhook has set active status, use profile directly
    if (isActive && tier) {
      // Use cached subscription_end (24h TTL) instead of calling
      // check-subscription on every auth event. The edge function is now
      // invoked only on demand from Pricing/Settings via refreshSubscription.
      const subscriptionEnd = readCachedSubEnd(currentProfile.user_id);
      setSubscription({ subscribed: true, tier, status, subscriptionEnd, isLoading: false });
      return;
    }

    // Fall back: profile has subscription_active set by admin or webhook
    if (currentProfile.subscription_active && currentProfile.subscription_tier) {
      setSubscription({
        subscribed: true,
        tier: currentProfile.subscription_tier as SubscriptionTierKey,
        status: status || "active",
        subscriptionEnd: null,
        isLoading: false,
      });
      return;
    }

    setSubscription({ subscribed: false, tier: null, status: status || "inactive", subscriptionEnd: null, isLoading: false });
  }, []);

  const refreshProfile = async () => {
    if (user) {
      const [profileData, fetchedRoles] = await Promise.all([fetchProfile(user.id), fetchRoles(user.id)]);
      await deriveSubscription(profileData, fetchedRoles);
    }
  };

  const refreshSubscription = useCallback(async () => {
    if (!profile) return;
    // Narrowed select — only the columns deriveSubscription consumes.
    const { data } = await supabase
      .from("profiles")
      .select("subscription_tier, subscription_active, subscription_status, proposals_used_this_month, is_suspended, avatar_url")
      .eq("user_id", profile.user_id)
      .single();
    const freshProfile = data ? ({ ...profile, ...data } as Profile) : profile;
    setProfile(freshProfile);

    // Fetch fresh subscription_end from Stripe via edge function, then cache.
    try {
      const { data: subData } = await supabase.functions.invoke("check-subscription");
      const end = subData?.subscription_end ?? null;
      writeCachedSubEnd(profile.user_id, end);
    } catch {
      /* non-critical */
    }
    await deriveSubscription(freshProfile, roles);
  }, [profile, roles, deriveSubscription]);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setTimeout(async () => {
            const [profileData, fetchedRoles] = await Promise.all([
              fetchProfile(session.user.id),
              fetchRoles(session.user.id),
            ]);
            setIsLoading(false);
            // Derive subscription after profile is loaded
            await deriveSubscription(profileData, fetchedRoles);
            // Auto-accept any pending team invites — but only once per browser
            // session per user. Previously this fired on every auth state change
            // (login, token refresh, tab focus), which burned Cloud usage for
            // users with no pending invites.
            try {
              const inviteGuardKey = `sf_team_invite_checked_${session.user.id}`;
              const hasInviteParam = typeof window !== "undefined" &&
                /[?&]invite=/.test(window.location.search);
              const alreadyChecked = typeof sessionStorage !== "undefined" &&
                sessionStorage.getItem(inviteGuardKey) === "1";
              if (hasInviteParam || !alreadyChecked) {
                await supabase.functions.invoke("team-invite", {
                  body: { action: "accept-invite" },
                });
                try { sessionStorage.setItem(inviteGuardKey, "1"); } catch { /* ignore */ }
              }
            } catch {
              // Non-critical
            }
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setSubscription({ subscribed: false, tier: null, status: null, subscriptionEnd: null, isLoading: false });
          setIsLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (!session?.user) setIsLoading(false);
    });

    return () => authSub.unsubscribe();
  }, []);

  // Background refresh: webhook is source of truth for active subs, so only
  // poll users without an active/trialing sub. New accounts (<3 days old)
  // poll every 3 hours to catch first checkout; everyone else once per day.
  const shouldPoll =
    !!user && !!profile && subscription.status !== "active" && subscription.status !== "trialing";
  const accountAgeMs = profile ? Date.now() - new Date(profile.created_at).getTime() : 0;
  const isNewAccount = !!profile && accountAgeMs < 3 * 24 * 60 * 60_000;
  const pollIntervalMs = isNewAccount ? 3 * 60 * 60_000 : 24 * 60 * 60_000;
  useVisibilityPolling(refreshSubscription, pollIntervalMs, shouldPoll);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setSubscription({ subscribed: false, tier: null, status: null, subscriptionEnd: null, isLoading: false });
  };

  const isSuperAdmin = roles.includes("super_admin");
  const isSupportRole = roles.includes("support_staff") || roles.includes("support_manager");
  const isSalesRole = roles.includes("sales_rep");
  const isDemoUser = roles.includes("demo_user");

  return (
    <AuthContext.Provider
      value={{ user, session, profile, roles, isLoading, isSuperAdmin, isSupportRole, isSalesRole, isDemoUser, subscription, signOut, refreshProfile, refreshSubscription }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
